import React, { FC } from "react"
import ContentTypes from "../../utils/ContentTypes"
import { Modal } from "./content-types/modal"
import {
  IBlockQuote,
  IContainer,
  IContentBlock,
  IContentBlockWithIcon,
  ITwoColumnBulletList,
  ITwoColumnLayout,
  IModal
} from "contentful-types"
import { ContentfulConditionalGate } from "../../lib/contentful/components/ContentfulConditionGate"
import { ContentfulContentProp } from "./content-types/shared-types"
import { Markdown, MarkdownProps, defaultMarkdownElements } from "services/markdown"
import { Box, Typography, UnorderedList } from "@carrotfertility/carotene-core"
import { LinkModalContextProvider, LinkToModal, ModalFromLink } from "./custom-components"

export type ContentfulContentTypes =
  | IBlockQuote
  | IContainer
  | IContentBlock
  | IContentBlockWithIcon
  | ITwoColumnBulletList
  | ITwoColumnLayout
  | IModal

type RecursiveContentfulContentRendererProps = {
  contents: Array<ContentfulContentTypes>
  childClasses?: string | null
  asList?: boolean | false
}

export type ContentfulContentRendererProps = RecursiveContentfulContentRendererProps

export function ContentfulContentRenderer(props: ContentfulContentRendererProps) {
  return (
    <LinkModalContextProvider>
      <RecursiveContentfulContentRenderer {...props} />
    </LinkModalContextProvider>
  )
}

export const RecursiveContentfulContentRenderer: FC<RecursiveContentfulContentRendererProps> = ({
  contents,
  asList = false
}) => {
  return (
    <>
      {contents?.map(
        (content) =>
          content.fields && (
            <ContentfulConditionalGate conditions={content.fields.conditions} key={content.sys.id}>
              {asList ? (
                <defaultMarkdownElements.li.component {...defaultMarkdownElements.li.props}>
                  <Content content={content} />
                </defaultMarkdownElements.li.component>
              ) : (
                <Content content={content} />
              )}
            </ContentfulConditionalGate>
          )
      )}
    </>
  )
}

function ContentfulMarkdownConsumer(props: MarkdownProps) {
  return (
    <Markdown
      overrides={{
        LinkToModal,
        ModalFromLink
      }}
      {...props}
    />
  )
}

const Content: FC<ContentfulContentProp<ContentfulContentTypes>> = ({ content }) => {
  switch (content.sys.contentType.sys.id) {
    case ContentTypes.CONTENT_BLOCK_WITH_ICON:
      return (
        <Box
          display="flex"
          gap={(theme) => theme.spacing(theme.tokens.spacing.md)}
          paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
          paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}
          justifyContent="space-between"
        >
          {(content as IContentBlockWithIcon).fields.icon?.fields && (
            <Box
              component="img"
              src={(content as IContentBlockWithIcon).fields.icon.fields.file?.url}
              height={60}
              width={60}
            />
          )}
          <Typography variant="body2">{(content as IContentBlockWithIcon).fields?.body}</Typography>
        </Box>
      )
    case ContentTypes.TWO_COLUMN_LAYOUT:
      return (
        <Box
          display="flex"
          gap={(theme) => theme.spacing(theme.tokens.spacing.md)}
          flexWrap="wrap"
          sx={(theme) => ({
            "& > *": { width: { xs: "100%", md: `calc(50% - ${theme.spacing(theme.tokens.spacing.md)})` } }
          })}
        >
          <RecursiveContentfulContentRenderer contents={(content as ITwoColumnLayout).fields?.content} />
        </Box>
      )
    case ContentTypes.TWO_COLUMN_BULLET_LIST:
      return (
        <UnorderedList
          component="ul"
          sx={(theme) => ({
            display: "flex",
            gap: theme.spacing(theme.tokens.spacing.md),
            flexWrap: "wrap",
            li: {
              width: { xs: "100%", md: `calc(50% - ${theme.spacing(theme.tokens.spacing.md)})` }
            }
          })}
        >
          <RecursiveContentfulContentRenderer contents={(content as ITwoColumnBulletList).fields?.content} asList />
        </UnorderedList>
      )

    case ContentTypes.BLOCK_QUOTE:
      return (
        <defaultMarkdownElements.blockquote.component {...defaultMarkdownElements.blockquote.props}>
          <defaultMarkdownElements.span.component {...defaultMarkdownElements.span.props}>
            <RecursiveContentfulContentRenderer contents={(content as IBlockQuote).fields?.content} />
          </defaultMarkdownElements.span.component>
        </defaultMarkdownElements.blockquote.component>
      )
    case ContentTypes.CONTAINER:
      return <RecursiveContentfulContentRenderer contents={(content as IContainer).fields?.content} />
    case ContentTypes.MODAL:
      return <Modal content={content as IModal} />
    case ContentTypes.CONTENT_BLOCK:
      return <ContentfulMarkdownConsumer>{(content as IContentBlock).fields?.body}</ContentfulMarkdownConsumer>
  }
  return null
}
